/**
 * @module app.core
 */

import { Aurelia, autoinject, PLATFORM, useView } from 'aurelia-framework';
import { NavigationInstruction, RouteConfig, Router } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Api, AppSession } from 'app/shared/lib/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { State } from 'app/shared/lib/state';
import { ISignOnProvider } from 'app/core/models/essette-interfaces';

/**
 * Can either use a `profile`, or a `provider` + `application` + optional `tenant`
 */
export interface SignOnCommand {
    profile?: {
        code: string;
    },
    provider?: {
        code: string;
    },
    application?: {
        code: string;
    },
    tenant?: {
        code: string;
    },
    token: {
        type: string;
        value: string;
    }
}

/**
 * A common foundation for pages that implement specific sign on flows
 */
@autoinject()
export abstract class SignonBase {

    ////****************** Fields and Properties ************************************////
    protected readonly app!: Aurelia;
    protected router!: Router;
    protected readonly events!: EventAggregator;
    protected readonly api!: Api;
    protected readonly session!: AppSession;
    protected readonly logger!: AppLogger;
    protected readonly env!: EnvironmentService;
    protected readonly i18n!: I18N;

    protected readonly busyState: State = new State();
    /** Whether External Sign In is in process */
    protected signingIn: boolean = false;
    protected statusMessage: string | null = null;
    protected errorMessage: string | null = null;
    protected signOnProvider?: ISignOnProvider;
    protected userName?: string;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        app?: Aurelia,
        router?: Router,
        events?: EventAggregator,
        api?: Api,
        session?: AppSession,
        logger?: AppLogger,
        env?: EnvironmentService,
        i18n?: I18N,
    ) {
        this.app = app!;
        this.router = router!;
        this.events = events!;
        this.api = api!;
        this.session = session!;
        this.logger = logger!;
        this.env = env!;
        this.i18n = i18n!;
        this.logger.source = this;
    }

    protected async activate(params: any, routeConfig: RouteConfig): Promise<void> {
        try {
            this.busyState.beginLoad();

            const { provider } = params;
            if(provider !== undefined) {
                // strip the param and swap the URL
                params.provider = null;
                this.router.navigateToRoute(routeConfig.name!, params, { trigger: false, replace: true });
            }
            else {
                throw new Error("Missing sign on provider parameter");
            }

            if(this.session.tenant != null) { this.api.logOut(); }

            this.signingIn = true;

            this.loadSignOnProvider(provider);
            if(!this.signOnProvider) {
                throw new Error(`Unknown Sign On provider: ${provider}`);
            }
        }
        catch(error) {
            this.logger.error(error);
            this.errorMessage = "Unknown Sign On provider";
            this.signingIn = false;
        }
        finally {
            this.busyState.endLoad();
        }
    }

    ////****************** Functions ************************************************////
    /**
     * Load a Sign On Provider
     */
    protected loadSignOnProvider(key: string): void {
        const signOnProviders = this.env.config.signOnProviders;
        this.signOnProvider = signOnProviders?.find(p => p.key === key);
    }

    /**
     * Initiate the external Sign On process for a given Sign On Provider
     */
    protected abstract signOn(): Promise<void>;

    /**
     * Validate a Sign On
     * @param signOnCommand
     * @returns
     */
    protected async validateSignOn(signOnCommand: SignOnCommand): Promise<void> {
        try {
            this.statusMessage = this.i18n.tr('core:local.sso.authenticating');

            const authenticationResult = await this.api.signOn(signOnCommand);

            const state = authenticationResult.state || 'unknown';
            if(state === 'unauthenticated' || state === 'checkInRequired') {
                // if API didn't authenticate or failed to pick a single tenant/context
                await this.api.logOut();
                throw new Error(`Unexpected sign on state: ${authenticationResult.state}`);
            }

            // Set "is sso" flag on session (prevents needing password on lockout screen)
            this.session.isSso = true;

            const body = document.querySelector('[aurelia-app]');
            const start = body!.getAttribute('start');

            const landingPage = authenticationResult?.result?.targetUrl || '/home';
            this.router.navigate(landingPage, { trigger: false, replace: true });
            await this.app.setRoot(start!);
        }
        catch(error) {
            this.logger.error(error);
            this.session.clear('error');
            this.errorMessage = this.i18n.tr('core:local.sso.errorAuthenticating');
        }
    }

}
